import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Hexagon, MousePointerClick, TrendingUp, Lock, RefreshCw, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "hg_dashboard_key";

const fmtHour = (h) => `${String(h).padStart(2, "0")}:00`;

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <p data-testid={testid} className="mt-3 font-display text-4xl font-extrabold text-zinc-900 tabular-nums">
      {value}
    </p>
  </div>
);

export default function Dashboard() {
  const [key, setKey] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [input, setInput] = useState("");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState("");

  const load = useCallback(async (k) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/stats`, { params: { key: k } });
      setStats(res.data);
      sessionStorage.setItem(SESSION_KEY, k);
    } catch {
      setStats(null);
      setError("Invalid key — access denied.");
      sessionStorage.removeItem(SESSION_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] honey-grain grid place-items-center px-5" data-testid="dashboard-gate">
        <div className="w-full max-w-sm rounded-3xl border border-amber-100 bg-white p-8 shadow-xl">
          <div className="grid place-items-center w-12 h-12 rounded-2xl bg-amber-400 text-zinc-900">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-zinc-900">Owner stats</h1>
          <p className="mt-2 text-sm text-zinc-600">Enter your private dashboard key to view link performance.</p>
          <form
            className="mt-6 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setKey(input.trim());
            }}
          >
            <input
              data-testid="dashboard-key-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Dashboard key"
              className="w-full rounded-xl border border-zinc-200 bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
            />
            {error && (
              <p data-testid="dashboard-error" className="text-sm font-semibold text-red-600">{error}</p>
            )}
            <button
              data-testid="dashboard-unlock-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-zinc-900 py-3 font-display text-sm font-bold text-white hover:bg-amber-500 hover:text-zinc-900 transition-colors disabled:opacity-60"
            >
              {loading ? "Checking…" : "Unlock dashboard"}
            </button>
          </form>
          <Link to="/" className="mt-5 block text-center text-xs text-zinc-500 hover:text-amber-600 transition-colors" data-testid="dashboard-back-link">
            ← Back to the page
          </Link>
        </div>
      </div>
    );
  }

  const maxHour = Math.max(...stats.hours.map((h) => h.count), 0);

  return (
    <div className="min-h-screen bg-[#FAF8F5] honey-grain" data-testid="dashboard">
      <header className="border-b border-amber-100 bg-white/80 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg text-zinc-900" data-testid="dashboard-logo">
            <span className="grid place-items-center w-8 h-8 rounded-xl bg-amber-400 text-zinc-900">
              <Hexagon className="w-5 h-5" strokeWidth={2.5} />
            </span>
            HoneyGain<span className="text-amber-600">Deals</span>
            <span className="hidden sm:inline-flex ml-2 rounded-full bg-amber-100 border border-amber-300 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">Stats</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="stats-email-report-button"
              onClick={async () => {
                setEmailStatus("sending");
                try {
                  await axios.post(`${API}/stats/email-report`, null, { params: { key } });
                  setEmailStatus("sent");
                } catch {
                  setEmailStatus("error");
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 px-3 py-2 text-xs sm:px-4 sm:text-sm font-semibold text-white hover:bg-amber-500 hover:text-zinc-900 transition-colors"
            >
              <Mail className="w-4 h-4" /> Email report
            </button>
            <button
              data-testid="stats-refresh-button"
              onClick={() => load(key)}
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs sm:px-4 sm:text-sm font-semibold text-zinc-700 hover:border-amber-400 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-zinc-900">Link performance</h1>
        <p className="mt-2 text-sm text-zinc-600">Clicks on your affiliate buttons, updated in real time. All times UTC.</p>
        {emailStatus === "sent" && (
          <p data-testid="stats-email-status" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700">
            Report emailed to your inbox — check geokath11@gmail.com
          </p>
        )}
        {emailStatus === "sending" && (
          <p data-testid="stats-email-status" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-4 py-2 text-sm font-semibold text-amber-700">
            Sending report…
          </p>
        )}
        {emailStatus === "error" && (
          <p data-testid="stats-email-status" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-4 py-2 text-sm font-semibold text-red-600">
            Could not send the report — please try again.
          </p>
        )}

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={MousePointerClick} label="Honeygain — all time" value={stats.total_clicks.toLocaleString("en-US")} testid="stats-total" />
          <StatCard icon={TrendingUp} label="Honeygain — this week" value={stats.week_clicks} testid="stats-week" />
          <StatCard icon={MousePointerClick} label="TrafficAdBar — all time" value={(stats.trafficadbar_total ?? 0).toLocaleString("en-US")} testid="stats-tab-total" />
          <StatCard icon={TrendingUp} label="TrafficAdBar — this week" value={stats.trafficadbar_week ?? 0} testid="stats-tab-week" />
        </div>

        {stats.week_clicks === 0 && (stats.trafficadbar_week ?? 0) === 0 && (
          <p className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-800" data-testid="stats-empty-note">
            No clicks recorded yet this week — share your page link to get the first ones.
          </p>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm" data-testid="stats-daily-chart">
            <h2 className="font-display text-lg font-bold text-zinc-900">Honeygain — clicks per day</h2>
            <p className="text-xs text-zinc-500 mt-1">Last 7 days · today: {stats.today_clicks}</p>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1EEE9" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 12, fill: "#71717A" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#71717A" }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip cursor={{ fill: "#FEF3C7" }} formatter={(v) => [`${v} clicks`, ""]} labelFormatter={(d) => `Date: ${d}`} />
                  <Bar dataKey="count" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm" data-testid="stats-tab-daily-chart">
            <h2 className="font-display text-lg font-bold text-zinc-900">TrafficAdBar — clicks per day</h2>
            <p className="text-xs text-zinc-500 mt-1">Last 7 days</p>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.tab_daily ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1EEE9" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 12, fill: "#71717A" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#71717A" }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip cursor={{ fill: "#E4E4E7" }} formatter={(v) => [`${v} clicks`, ""]} labelFormatter={(d) => `Date: ${d}`} />
                  <Bar dataKey="count" fill="#3F3F46" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm lg:col-span-2" data-testid="stats-hourly-chart">
            <h2 className="font-display text-lg font-bold text-zinc-900">Honeygain clicks by hour</h2>
            <p className="text-xs text-zinc-500 mt-1">
              All time, hour of day (UTC) — darkest bar is your busiest hour
              {stats.busiest_hour != null && `: ${fmtHour(stats.busiest_hour)}`}
            </p>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.hours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1EEE9" vertical={false} />
                  <XAxis dataKey="hour" tickFormatter={fmtHour} tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} interval={2} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#71717A" }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip cursor={{ fill: "#FEF3C7" }} formatter={(v) => [`${v} clicks`, ""]} labelFormatter={(h) => `Hour: ${fmtHour(h)}`} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {stats.hours.map((h) => (
                      <Cell key={h.hour} fill={h.count === maxHour && maxHour > 0 ? "#B45309" : "#FCD34D"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
